import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type AnyRoute,
  type RouterHistory,
} from "@tanstack/react-router";

import { bootstrap, isViewTab } from "@/api/session";
import { VIEW_ENDED_PATH } from "@/api/viewStore";
import { LoginPage } from "@/app/LoginPage";
import { NotFound } from "@/app/NotFound";
import { PublicLayout } from "@/app/PublicLayout";
import { RootLayout } from "@/app/RootLayout";
import {
  EMPLOYEE_HOME,
  HOME_PATH,
  pageRefusal,
  pages as registeredPages,
  platformRole,
  publicPages as registeredPublicPages,
  type PageDef,
} from "@/app/registry";
import { AppShell } from "@/app/shell/AppShell";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    title?: string;
    /** Who may open the page (see pageRefusal). */
    access?: Pick<
      PageDef,
      | "ownerOnly"
      | "aiAccess"
      | "raOnly"
      | "proOnly"
      | "employees"
      | "notForRaClients"
      | "friendsOnly"
    >;
  }
}

export interface RouterContext {
  queryClient: QueryClient;
}

function safeRedirect(target: unknown): string {
  // only same-app paths, never "//evil.example" or a full URL
  const ok =
    typeof target === "string" &&
    target.startsWith("/") &&
    !target.startsWith("//") &&
    !target.startsWith("/login") &&
    target.length < 2000;
  return ok ? target : HOME_PATH;
}

function pageRoute(parent: () => AnyRoute, p: PageDef) {
  return createRoute({
    getParentRoute: parent,
    path: p.path,
    component: p.component,
    staticData: {
      title: p.title,
      access: {
        ownerOnly: p.ownerOnly,
        aiAccess: p.aiAccess,
        raOnly: p.raOnly,
        proOnly: p.proOnly,
        employees: p.employees,
        notForRaClients: p.notForRaClients,
        friendsOnly: p.friendsOnly,
      },
    },
    ...(p.validateSearch ? { validateSearch: p.validateSearch } : {}),
    // Friends-only pages (RA calls, Explore, following; one kind of account,
    // 2026-10-25): anyone else goes Home before the page renders, quietly. A
    // <Navigate> from inside the page (RoleGate) loops, as it did for employees.
    ...(p.friendsOnly
      ? {
          beforeLoad: async () => {
            const session = await bootstrap();
            const user = session.user;
            if (
              session.status === "signed-in" &&
              pageRefusal(p, user?.role, user?.ai_calls_access === true) === "hidden"
            )
              throw redirect({ to: HOME_PATH, replace: true });
          },
        }
      : {}),
  });
}

/**
 * Public pages (no sign-in guard):
 * - a page that is also a signed-in page (the RA directory) goes under
 *   PublicLayout: the app shell when signed in, a light public frame when not;
 * - a public-only page (sign-up) brings its own chrome and sits next to /login.
 */
export function buildRouteTree(
  pages: PageDef[] = registeredPages,
  publicPages: PageDef[] = registeredPublicPages,
) {
  /** Pages an employee may open (their `employees` flag), as path prefixes. */
  const employeePrefixes = pages
    .filter((p) => p.employees)
    .map((p) => p.path.split("/$")[0] ?? p.path);
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: RootLayout,
    notFoundComponent: NotFound,
  });

  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/login",
    staticData: { title: "Sign in" },
    validateSearch: (s: Record<string, unknown>) => ({
      redirect: safeRedirect(s.redirect),
    }),
    beforeLoad: async ({ search }) => {
      const session = await bootstrap();
      if (session.status === "signed-in")
        throw redirect({ to: search.redirect });
    },
    component: LoginPage,
  });

  const appRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "app",
    beforeLoad: async ({ location }) => {
      const session = await bootstrap();
      if (session.status !== "signed-in") {
        // an admin's view tab (V044) whose view is over
        if (isViewTab()) throw redirect({ to: VIEW_ENDED_PATH });
        throw redirect({ to: "/login", search: { redirect: location.href } });
      }
      // Employees work only in the sales console (and their account): send
      // them there before any other page renders. Redirecting from inside a
      // page instead (RoleGate's <Navigate>) looped on the Home page.
      if (
        platformRole(session.user?.role) === "employee" &&
        !employeePrefixes.some(
          (pre) =>
            location.pathname === pre ||
            location.pathname.startsWith(`${pre}/`),
        )
      ) {
        throw redirect({ to: EMPLOYEE_HOME, replace: true });
      }
    },
    component: AppShell,
  });

  // "/" is the public landing page for visitors; signed-in users go on to the app's home.
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    staticData: { title: "Home" },
    beforeLoad: async () => {
      const session = await bootstrap();
      if (session.status === "signed-in") throw redirect({ to: HOME_PATH });
    },
    component: lazyRouteComponent(
      () => import("@/site/LandingPage"),
      "LandingPage",
    ),
  });

  // "/app" itself opens the app's home page.
  const appIndexRoute = createRoute({
    getParentRoute: () => appRoute,
    path: "/app",
    beforeLoad: () => {
      throw redirect({ to: HOME_PATH });
    },
  });

  const publicPaths = new Set(publicPages.map((p) => p.path));
  const appPaths = new Set(pages.map((p) => p.path));
  const pageRoutes = pages
    .filter((p) => !publicPaths.has(p.path))
    .map((p) => pageRoute(() => appRoute, p));

  const publicRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "public",
    beforeLoad: async () => {
      await bootstrap(); // settle the session so the layout picks the right frame
    },
    component: PublicLayout,
  });
  const sharedRoutes = publicPages
    .filter((p) => appPaths.has(p.path))
    .map((p) => pageRoute(() => publicRoute, p));
  const bareRoutes = publicPages
    .filter((p) => !appPaths.has(p.path))
    .map((p) => pageRoute(() => rootRoute, p));

  return rootRoute.addChildren([
    loginRoute,
    indexRoute,
    ...bareRoutes,
    publicRoute.addChildren(sharedRoutes),
    appRoute.addChildren([appIndexRoute, ...pageRoutes]),
  ]);
}

export function createAppRouter(
  queryClient: QueryClient,
  options: {
    history?: RouterHistory;
    pages?: PageDef[];
    publicPages?: PageDef[];
  } = {},
) {
  const router = createRouter({
    routeTree: buildRouteTree(options.pages, options.publicPages),
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0, // TanStack Query owns caching
    scrollRestoration: true,
    ...(options.history ? { history: options.history } : {}),
  });
  keepScrollOnSamePage(router);
  return router;
}

/**
 * A filter, tab or dropdown that only changes the URL's search keeps the page where it
 * is (owner, 30 Oct 2026: every selection jumped to the top). The router scrolls to the
 * top on every navigation unless told otherwise, so a change on the same path defaults
 * to not resetting; going to another page still starts at the top.
 */
export function keepScrollOnSamePage(router: { commitLocation: (o: never) => Promise<void>; latestLocation: { pathname: string } }) {
  const commit = router.commitLocation;
  router.commitLocation = ((next: { pathname: string; resetScroll?: boolean }) =>
    commit({
      ...next,
      resetScroll: next.resetScroll ?? (next.pathname === router.latestLocation.pathname ? false : undefined),
    } as never)) as never;
}
